/**
 * Pure decision helpers for incoming-call handling in the /order view.
 * Kept free of Redux/socket dependencies so they can be unit-tested in isolation.
 */

export interface OrderFormSnapshot {
  loadedCustomer: unknown | null;
  cartItemCount: number;
  orderFormFields: Record<string, string | null | undefined> | null;
}

export interface CallPayload {
  uuid: string;
  finished_at?: string | null;
}

/**
 * Returns true when the operator has work in the /order view that would be
 * lost by silently loading a new caller's data. Callers in this state should
 * be prompted via modal instead of overwriting the form.
 */
export function isOrderInProgress(state: OrderFormSnapshot): boolean {
  if (state.loadedCustomer !== null) return true;
  if (state.cartItemCount > 0) return true;
  const f = state.orderFormFields;
  if (f && Object.values(f).some((v) => typeof v === 'string' && v.trim() !== '')) {
    return true;
  }
  return false;
}

export type IncomingCallDecision = 'load-immediately' | 'prompt-via-modal';

/**
 * Decides what to do when a new CALL event is pushed to the operator's
 * agent socket. If no order is in progress we load immediately, otherwise
 * we buffer the call and show the decision modal.
 */
export function decideIncomingCall(state: OrderFormSnapshot): IncomingCallDecision {
  return isOrderInProgress(state) ? 'prompt-via-modal' : 'load-immediately';
}

/**
 * Returns true when the buffered "pending" call should be discarded because
 * the matching PUSH_CALL event reports the call has finished. After this
 * returns true the buffer should be cleared and any open decision modal
 * dismissed — we only auto-load calls that are still ongoing.
 */
export function shouldDropPendingOnPush(
  pendingUuid: string | null | undefined,
  pushPayload: CallPayload,
): boolean {
  return !!pendingUuid && pushPayload.uuid === pendingUuid && !!pushPayload.finished_at;
}

/**
 * Returns true when the currently-loaded call matches the incoming PUSH_CALL
 * payload — used to sync loadedCall with backend updates (most importantly
 * finished_at, which flips the UI into "call ended" state). We do NOT clear
 * loadedCall on finish: the operator may still be editing the form, and the
 * delivery submit must attach call.id so the delivery is linked back to the
 * call it originated from.
 */
export function shouldUpdateLoadedCallOnPush(
  loadedCallUuid: string | null | undefined,
  pushPayload: CallPayload,
): boolean {
  return !!loadedCallUuid && pushPayload.uuid === loadedCallUuid;
}
